/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// เลือกรูปจากแกลเลอรีสินค้าแบบตาราง (18 ต.ค. 2569 · เจ้าของงานขอ "ใช้นาน ๆ แล้วรูปเยอะ")
// รูปหลักขึ้นก่อนและมีครั้งเดียว · ที่เหลือใหม่ก่อน
// โหมดเดียว (จัดการภาพ): กดแล้วปิดเอง · โหมดหลายรูป (ภาพ AI): ติ๊กหลายรูปแล้วกด "ใส่ N รูป"
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ButtonBase from '@mui/material/ButtonBase';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import * as Func from 'services/Function';
import nopic from 'assets/images/nopic.jpg';

const dateText = (v) => (v ? Func.dateDisplay(String(v).replace(' ', 'T')) : '');
const onImgError = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = nopic;
};

export default function ImagePickerDialog({
  open,
  images,
  main,
  value,
  onSelect,
  onClose,
  multiple,
  disabledIds,
  onConfirm,
  title,
}) {
  const mainId = String(main.id || '');
  const mainRow = images.find((im) => String(im.id) === mainId);
  const [sel, setSel] = useState([]); // โหมดหลายรูป — id ที่ติ๊กไว้
  useEffect(() => {
    if (open) setSel([]);
  }, [open]);

  const items = [
    ...(main.url
      ? [
          {
            key: 'main',
            id: mainId,
            url: main.url,
            label: 'รูปหลัก',
            title: mainId ? `รูป #${mainId}` : '',
            date: dateText(mainRow && mainRow.created_at),
          },
        ]
      : []),
    ...images
      .filter((im) => String(im.id) !== mainId)
      .map((im, i) => ({
        key: String(im.id),
        id: String(im.id),
        url: im.image_url,
        label: `รูปที่ ${i + (main.url ? 2 : 1)}`,
        title: `รูป #${im.id}`,
        date: dateText(im.created_at),
      })),
  ];
  const isOn = (it) => (multiple ? sel.includes(it.id) : String(value) === it.key);
  const isUsed = (it) => multiple && disabledIds.includes(it.id);
  const pick = (it) => {
    if (!multiple) {
      onSelect(it.key);
      onClose();
      return;
    }
    if (isUsed(it) || !it.id) return;
    setSel((cur) => (cur.includes(it.id) ? cur.filter((x) => x !== it.id) : [...cur, it.id]));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      aria-labelledby='image-picker-title'
    >
      <DialogTitle id='image-picker-title'>
        {title}
        <MDTypography component='span' variant='button' color='text' sx={{ ml: 1, opacity: 0.7 }}>
          {items.length} รูป
        </MDTypography>
      </DialogTitle>
      <DialogContent dividers>
        {items.length === 0 ? (
          <MDTypography variant='body2' color='text'>
            สินค้านี้ยังไม่มีรูปในแกลเลอรี
          </MDTypography>
        ) : (
          <MDBox
            display='grid'
            gap={1.5}
            sx={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}
          >
            {items.map((it) => {
              const on = isOn(it);
              const used = isUsed(it);
              return (
                <ButtonBase
                  key={it.key}
                  aria-pressed={on}
                  disabled={used}
                  aria-label={`เลือก${it.label}${used ? ' · ใส่แล้ว' : ''}`}
                  title={it.title || undefined}
                  onClick={() => pick(it)}
                  sx={{
                    display: 'block',
                    textAlign: 'left',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: on ? 'info.main' : 'grey.300',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    opacity: used ? 0.45 : 1,
                    '&:hover': { borderColor: 'info.main' },
                    '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'info.main' },
                  }}
                >
                  <MDBox
                    position='relative'
                    sx={{ aspectRatio: '1 / 1', bgcolor: 'grey.100', display: 'flex' }}
                  >
                    <MDBox
                      component='img'
                      src={it.url}
                      alt=''
                      loading='lazy'
                      onError={onImgError}
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                    {(on || used) && (
                      <Icon
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          color: 'info.main',
                          bgcolor: '#fff',
                          borderRadius: '50%',
                        }}
                      >
                        check_circle
                      </Icon>
                    )}
                  </MDBox>
                  <MDBox px={1} py={0.75}>
                    <MDTypography
                      variant='caption'
                      fontWeight='bold'
                      color={it.key === 'main' ? 'info' : 'dark'}
                      display='block'
                    >
                      {it.label}
                      {used ? ' · ใส่แล้ว' : ''}
                    </MDTypography>
                    {it.date && (
                      <MDTypography variant='caption' color='text' display='block'>
                        {it.date}
                      </MDTypography>
                    )}
                  </MDBox>
                </ButtonBase>
              );
            })}
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton variant='outlined' color='dark' size='small' onClick={onClose}>
          {multiple ? 'ยกเลิก' : 'ปิด'}
        </MDButton>
        {multiple && (
          <MDButton
            variant='gradient'
            color='info'
            size='small'
            disabled={!sel.length}
            onClick={() => onConfirm(sel)}
          >
            ใส่ {sel.length || ''} รูป
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
ImagePickerDialog.defaultProps = {
  value: '',
  onSelect: () => {},
  multiple: false,
  disabledIds: [],
  onConfirm: () => {},
  title: 'เลือกรูปต้นทาง',
};
ImagePickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      image_url: PropTypes.string,
      created_at: PropTypes.string,
    })
  ).isRequired,
  main: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    url: PropTypes.string,
  }).isRequired,
  value: PropTypes.string, // โหมดเดียว: 'main' หรือ id ที่เลือกอยู่
  onSelect: PropTypes.func, // โหมดเดียว
  onClose: PropTypes.func.isRequired,
  multiple: PropTypes.bool, // ติ๊กหลายรูปแล้วกดยืนยัน
  disabledIds: PropTypes.arrayOf(PropTypes.string), // โหมดหลายรูป: id ที่ใส่ไปแล้ว
  onConfirm: PropTypes.func, // โหมดหลายรูป: (ids[]) =>
  title: PropTypes.string,
};
